<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { getHostNameFromURL } from 'dashboard/helper/URLHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  customDomain: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

// Get target domain from FRONTEND_URL
const domain = computed(() => {
  const { hostURL } = window?.chatwootConfig || {};
  return getHostNameFromURL(hostURL) || '';
});

const cnameRecord = computed(
  () => `${props.customDomain} CNAME ${domain.value}`
);

const handleCopy = async e => {
  e.stopPropagation();
  await copyTextToClipboard(cnameRecord.value);
  useAlert(t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DNS_DIALOG.COPY_SUCCESS'));
};

const dialogRef = ref(null);

const onClose = () => {
  emit('close');
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <NextButton
      icon="i-lucide-x"
      sm
      ghost
      slate
      class="flex-shrink-0 absolute top-2 ltr:right-2 rtl:left-2"
      @click="onClose"
    />

    <div class="flex flex-col gap-6 divide-y divide-n-strong">
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2 ltr:pr-10 rtl:pl-10">
          <h3 class="text-base font-medium leading-6 text-n-slate-12">
            {{ t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DNS_DIALOG.HEADER') }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-12">
            {{ t('INBOX_MGMT.CSAT.CUSTOM_DOMAIN.DNS_DIALOG.DESCRIPTION') }}
          </p>
        </div>

        <!-- CNAME Record Display with Copy Button -->
        <div class="flex items-center gap-3 w-full">
          <span
            class="min-h-10 px-3 py-2.5 inline-flex items-center w-full text-sm bg-transparent border rounded-lg text-n-slate-11 border-n-strong"
          >
            {{ cnameRecord }}
          </span>
          <NextButton
            faded
            slate
            type="button"
            icon="i-lucide-copy"
            class="flex-shrink-0"
            @click="handleCopy"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
